import React from 'react';
import { Box, BoxProps } from '@mui/material';
import FilterChips from '../filter-chips/filter-chips';
import { useMapStyle } from '../../../contexts/map-style-context';

interface MapStyleSelectorProps {
  /** Label shown above the chips. Defaults to "Map Style" */
  label?: React.ReactNode;
  /** Additional Box props for the container */
  sx?: BoxProps['sx'];
}

/**
 * A reusable component for selecting map styles.
 * Uses the MapStyleContext for state management and localStorage persistence.
 * 
 * Must be used within a MapStyleProvider.
 * 
 * @example
 * ```tsx
 * <MapStyleSelector />
 * <MapStyleSelector label="Style" sx={{ mb: 2 }} />
 * ```
 */
export default function MapStyleSelector({ 
  label = "Map Style",
  sx 
}: MapStyleSelectorProps) {
  const { mapStyle, setMapStyle, options } = useMapStyle();

  const handleChange = (selected: { value: string; label: string }[]) => {
    if (selected.length > 0) {
      setMapStyle(selected[0].value as typeof mapStyle);
    }
  };

  return (
    <Box sx={sx}>
      <FilterChips
        label={label}
        options={options}
        selected={options.filter(opt => opt.value === mapStyle)}
        onChange={handleChange}
        getOptionLabel={(opt) => opt.label}
        optionsEqual={(a, b) => a.value === b.value}
      />
    </Box>
  );
}

